import { Component, OnDestroy, OnInit } from '@angular/core';
import { TokenService } from '../services/token.service';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { LogService } from '../services/log.service';
import { SipService } from '../services/sip.service';
import {
  BehaviorSubject,
  catchError,
  Subject,
  switchMap,
  takeUntil,
  throwError,
} from 'rxjs';
import { Status } from '../../generated/eveportal-api/graphql';
import { UserPresenceState } from '../enums/user-presence-state.enum';
import { ContactService } from '../services/contact.service';
import { MatDialog } from '@angular/material/dialog';
import { UserProfileDialogComponent } from '../user-profile-dialog/user-profile-dialog.component';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  selectedTab: 'contacts' | 'history' = 'contacts';

  presenceClassSubject: BehaviorSubject<string> = new BehaviorSubject<string>(
    'presence-none'
  );
  presenceClass$ = this.presenceClassSubject.asObservable();

  avatarUrl: string = 'assets/eveuser-avatar.png';
  userPresentState: UserPresenceState = UserPresenceState.OutOfOffice;
  eveUser!: any;

  private destroySubject = new Subject<void>();

  constructor(
    private tokenService: TokenService,
    private logService: LogService,
    private sipService: SipService,
    private authService: AuthService,
    private contactService: ContactService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) {
    this.isAuthenticated =
      this.tokenService.isEveportalJwtTokenValid() &&
      this.tokenService.isRefreshTokenValid();
    this.setPresenceClass();
    this.loadAvatar();
    this.setEveUser();
  }
  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  ngOnInit() {}

  downloadEncryptedLog() {
    this.logService.downloadAllLogsAsZip();
  }

  setPresenceClass() {
    this.sipService.userPresence$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (status: UserPresenceState) => {
          this.userPresentState = status;
          switch (status) {
            case UserPresenceState.Available:
              this.presenceClassSubject.next('presence-green');
              break;
            case UserPresenceState.Busy:
            case UserPresenceState.Lunch:
            case UserPresenceState.Meeting:
              this.presenceClassSubject.next('presence-red');
              break;
            case UserPresenceState.Holiday:
            case UserPresenceState.OutOfOffice:
              this.presenceClassSubject.next('presence-black');
              break;
            default:
              this.presenceClassSubject.next('presence-grey');
          }
        },
      });
  }

  loadAvatar() {
    const eveUserName =
      this.tokenService.getLoginRefreshToken().eveUser.eveUserName;
    console.log(eveUserName);
    this.contactService
      .getAvatar(eveUserName)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          this.avatarUrl = this.sanitizer.bypassSecurityTrustUrl(
            objectUrl
          ) as string;
        },
        error: () => {
          this.avatarUrl = 'assets/eveuser-avatar.png';
        },
      });
  }

  openUserProfile(): void {
    // Create array of presence states with key-value pairs
    const presenceStates = Object.keys(UserPresenceState)
      .filter((key) => isNaN(Number(key))) // Filter out numeric keys
      .map((key) => ({
        key: UserPresenceState[key as keyof typeof UserPresenceState],
        value:
          UserPresenceState[key as keyof typeof UserPresenceState].valueOf(),
      }));

    const dialogRef = this.dialog.open(UserProfileDialogComponent, {
      width: '450px',
      data: {
        eveUserId: this.eveUser.eveUserId,
        firstName: this.eveUser.firstName,
        surname: this.eveUser.surname,
        username: this.eveUser.eveUserName,
        phoneNumber: this.eveUser.number?.number,
        internalNumber: this.eveUser.internalNumber?.number,
        avatarUrl: this.avatarUrl,
        presenceClass$: this.presenceClass$,
        currentPresence: this.userPresentState,
        availablePresenceStates: presenceStates,
      },
      hasBackdrop: true,
      backdropClass: 'dialog-backdrop',
      panelClass: 'user-profile-dialog-panel',
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  private setEveUser() {
    this.tokenService.eveUser$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (eveUser) => {
          this.eveUser = eveUser;
        },
      });
  }
}
